(function () {
  'use strict';

  // Cache to avoid re-fetching product data for the same handle
  const productCache = {};

  // Pending requests: handle -> Promise, to avoid duplicate in-flight fetches
  const pendingFetches = {};

  // Debounce timer for yotpo refresh scheduling
  let yotpoRefreshTimer = null;
  // Whether a yotpo poll loop is already running
  let yotpoPollActive = false;

  function debounce(func, timeout = 300){
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => { func.apply(this, args); }, timeout);
    };
  }

  /**
   * Format cents value into a money string using Shopify's money_format.
   * Supports ${{amount}}, ${{amount_no_decimals}}, ${{amount_with_comma_separators}},
   * ${{amount_no_decimals_with_comma_separators}}.
   */
  function formatMoney(cents) {
    const moneyFormat = window.SS_MONEY_FORMAT || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    const amountNoDecimals = String(Math.floor(cents / 100));
    const amountWithCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const amountNoDecimalsWithCommas = amountNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return moneyFormat
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', amountNoDecimals)
      .replace('{{amount_with_comma_separators}}', amountWithCommas)
      .replace('{{amount_no_decimals_with_comma_separators}}', amountNoDecimalsWithCommas);
  }

  /**
   * Extract the product URL handle from a card element by finding its product link.
   */
  function getProductHandle(card) {
    const link = card.querySelector('a[href*="/products/"]');
    if (!link) return null;
    const match = link.getAttribute('href').match(/\/products\/([^?#/]+)/);
    return match ? match[1] : null;
  }

  // SVG icons for condition labels (no IDs, safe for multiple instances)
  const ICON_CPO = '<svg class="productitem--condition-icon" width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M11.4918 3.16133C4.6651 3.1322 0.999837 6.12086 0.686493 12.3065C0.682606 12.3844 0.561311 12.3888 0.549648 12.3116C0.244858 10.2907 0.30395 8.44829 0.851329 6.84764C2.65675 1.56213 9.54563 0.868852 14.4332 2.5416C14.6773 2.62535 14.7263 2.91955 14.5241 3.07175C10.7111 5.93007 11.2305 13.7302 1.95075 10.3497C3.40006 6.12669 6.37099 3.51015 11.4918 3.16133Z" fill="#25995C"/></svg>';
  const ICON_FA = '<svg class="productitem--condition-icon" width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.46462 0.380664C9.77295 0.113997 10.2313 0.113997 10.5396 0.380664L12.1209 1.74941C12.3 1.90566 12.5386 1.97546 12.774 1.94108L14.8438 1.64525C15.248 1.58691 15.6323 1.83483 15.748 2.22546L16.3375 4.23171C16.4052 4.45983 16.5677 4.64837 16.7844 4.74629L18.6865 5.61608C19.0573 5.78587 19.2469 6.20254 19.1323 6.59316L18.5438 8.60046C18.4771 8.82858 18.5125 9.07441 18.6407 9.27441L19.7709 11.0348C19.9907 11.3775 19.9261 11.8307 19.6177 12.0984L18.0375 13.4682C17.8584 13.6244 17.7552 13.8505 17.7552 14.088L17.7542 16.1796C17.7542 16.5869 17.4542 16.9327 17.0511 16.9911L14.9802 17.29C14.7448 17.3234 14.5365 17.4577 14.4073 17.6577L13.2761 19.4171C13.0552 19.7598 12.6167 19.889 12.2459 19.7192L10.3427 18.8515C10.1261 18.7525 9.87816 18.7525 9.6615 18.8515L7.75837 19.7192C7.38754 19.889 6.94899 19.7598 6.72816 19.4171L5.59691 17.6577C5.46774 17.4577 5.25837 17.3234 5.02399 17.29L2.95316 16.9911C2.55004 16.9327 2.25004 16.5869 2.25004 16.1796L2.24899 14.088C2.24899 13.8505 2.14587 13.6244 1.9667 13.4682L0.386495 12.0984C0.0781614 11.8307 0.013578 11.3775 0.23337 11.0348L1.36358 9.27441C1.4917 9.07441 1.52712 8.82858 1.46045 8.60046L0.871911 6.59316C0.757328 6.20254 0.946911 5.78587 1.31774 5.61608L3.21983 4.74629C3.43649 4.64837 3.59899 4.45983 3.66566 4.23171L4.25629 2.22546C4.37191 1.83483 4.75629 1.58691 5.16045 1.64525L7.23024 1.94108C7.46566 1.97546 7.7042 1.90566 7.88337 1.74941L9.46462 0.380664Z" fill="#0084E4"/><path fill-rule="evenodd" clip-rule="evenodd" d="M6.58664 10.0324C6.83039 9.79486 7.21998 9.80007 7.45643 10.0438L9.38039 12.0199C9.47727 12.1188 9.64393 12.0949 9.70852 11.9719L12.4241 6.81674C12.5825 6.5157 12.9544 6.40111 13.2554 6.55945C13.5564 6.71778 13.6721 7.08965 13.5127 7.3907L10.7971 12.5459C10.3429 13.4084 9.17831 13.5772 8.4981 12.8782L6.57518 10.9022C6.33768 10.6584 6.34289 10.2688 6.58664 10.0324Z" fill="white"/></svg>';

  /**
   * Fetch and cache product data via the Section Rendering API.
    * Returns: { id, compare_at_price_max, price_min, msrp, is_msrp_collection, condition_class, template_suffix }
   * Shopify wraps the response in <div id="shopify-section-...">...</div> — we extract
   * the inner text and parse it as JSON.
   */
  function fetchProduct(handle) {
    if (productCache[handle]) {
      return Promise.resolve(productCache[handle]);
    }
    if (pendingFetches[handle]) {
      return pendingFetches[handle];
    }
    const request = fetch('/products/' + handle + '?section_id=ss-product-data')
      .then(function (response) {
        if (!response.ok) throw new Error('Product fetch failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        const div = document.createElement('div');
        div.innerHTML = html;
        const inner = div.firstElementChild ? div.firstElementChild.textContent : html;
        const data = JSON.parse(inner.trim());
        productCache[handle] = data;
        delete pendingFetches[handle];
        return data;
      })
      .catch(function () {
        delete pendingFetches[handle];
        return null;
      });
    pendingFetches[handle] = request;
    return request;
  }

  /**
   * Call the appropriate Yotpo refresh API, supporting both legacy and new Yotpo SDKs.
   */
  function callYotpoRefresh() {
    // New Yotpo widgets SDK (v3)
    if (
      typeof window.yotpoWidgetsContainer !== 'undefined' &&
      typeof window.yotpoWidgetsContainer.initWidgets === 'function'
    ) {
      window.yotpoWidgetsContainer.initWidgets();
      return true;
    }
    // Legacy Yotpo SDK
    if (
      typeof window.yotpo !== 'undefined' &&
      typeof window.yotpo.refreshWidgets === 'function'
    ) {
      window.yotpo.refreshWidgets();
      return true;
    }
    return false;
  }

  /**
   * Schedule a Yotpo widget refresh. If Yotpo is not available yet, polls until it is
   * (up to ~5 seconds), then calls refresh.
   */
  function scheduleYotpoRefresh() {
    clearTimeout(yotpoRefreshTimer);
    yotpoRefreshTimer = setTimeout(function () {
      if (callYotpoRefresh()) return;

      // Yotpo not ready — start a poll loop (only one at a time)
      if (yotpoPollActive) return;
      yotpoPollActive = true;

      let attempts = 0;
      const maxAttempts = 25; // 25 × 200ms = 5 seconds

      const poll = setInterval(function () {
        attempts++;
        if (callYotpoRefresh() || attempts >= maxAttempts) {
          clearInterval(poll);
          yotpoPollActive = false;
        }
      }, 200);
    }, 300);
  }

  /**
   * Determine product condition from custom.class metafield (via Section Rendering API).
   * Returns 'certified-pre-owned', 'factory-authorized', or ''.
   */
  function getConditionType(product) {
    const cls = (product.condition_class || '').toLowerCase();
    if (cls.includes('factory authorized')) return 'factory-authorized';
    if (cls.includes('certified pre-owned')) return 'certified-pre-owned';

    const suffix = (product.template_suffix || '').toLowerCase();
    if (suffix.includes('factory-auth')) return 'factory-authorized';
    if (suffix === 'fresh-arrival' || suffix === 'non-fresh-arrival') return 'certified-pre-owned';

    return '';
  }

  function isFromRangeEnabled() {
    return (window.SS_RANGE_PRICE_FORMAT || 'default') === 'from';
  }

  function renderFromPrice(pricingEl, fromText) {
    if (!pricingEl || !fromText) return;

    pricingEl.innerHTML = '<span class="money" data-price data-ss-from-price>' + fromText + '</span>';
  }

  function hasOnlyFromPrice(pricingEl) {
    if (!pricingEl) return false;

    const fromNode = pricingEl.querySelector('[data-ss-from-price]');
    if (!fromNode) return false;

    const normalize = function (text) {
      return (text || '').replace(/\s+/g, ' ').trim();
    };

    return normalize(pricingEl.textContent) === normalize(fromNode.textContent);
  }

  function hasValidFromPriceState(pricingEl) {
    if (!pricingEl) return false;

    const fromNode = pricingEl.querySelector('[data-ss-from-price]');
    if (!fromNode) return false;

    const normalize = function (text) {
      return (text || '').replace(/\s+/g, ' ').trim();
    };

    const clone = pricingEl.cloneNode(true);
    clone.querySelectorAll(
      '.ss-injected-compare-price, .price__compare-at, [data-price-compare-at], [data-compare-at-price], s'
    ).forEach(function (el) {
      el.remove();
    });

    return normalize(clone.textContent) === normalize(fromNode.textContent);
  }

  function parseMsrpToCents(msrpValue) {
    if (!msrpValue) return 0;
    const n = Number(msrpValue);
    return isFinite(n) && n > 0 ? Math.round(n) : 0;
  }

  const PRICING_SELECTOR = [
    '.ss__custom-result__details__pricing',
    '.productitem__price',
    '.productitem--price',
    '.ss__price',
    '.ss__result__price',
  ].join(', ');

  function applyFromRangePriceImmediate(card) {
    if (!isFromRangeEnabled()) return;

    const pricingEl = card.querySelector(PRICING_SELECTOR);
    if (!pricingEl) return;

    if (pricingEl.dataset.ssFromApplied === '1' && hasValidFromPriceState(pricingEl)) return;

    const existingFromNode = pricingEl.querySelector('[data-ss-from-price]');

    const currentContainer = pricingEl.querySelector('.price__current, [data-price-container]');
    const minNode = currentContainer
      ? currentContainer.querySelector('[data-price-min], .price__current--min')
      : pricingEl.querySelector('[data-price-min], .price__current--min');
    let minText = minNode ? minNode.textContent.trim() : '';

    if (!minText && existingFromNode) {
      minText = (existingFromNode.textContent || '').replace(/^\s*From\s+/i, '').trim();
    }

    if (!minText) {
      const currentText = currentContainer ? (currentContainer.textContent || '') : (pricingEl.textContent || '');
      const splitRange = currentText.split(/\s[-–]\s/);
      if (splitRange.length > 1) {
        minText = splitRange[0].trim();
      }
    }

    if (!minText) {
      const fullText = pricingEl.textContent || '';
      const rangeMatch = fullText.match(/([$€£]\s?[\d.,]+)\s*[-–]/);
      if (rangeMatch) {
        minText = rangeMatch[1].trim();
      }
    }

    if (!minText) return;

    renderFromPrice(pricingEl, 'From ' + minText);
    pricingEl.dataset.ssFromApplied = '1';
  }

  function applyFromRangePrice(card, product) {
    if (!isFromRangeEnabled()) return;
    if (!product || !product.price_varies) return;

    const pricingEl = card.querySelector(PRICING_SELECTOR);

    if (!pricingEl) return;

    if (hasOnlyFromPrice(pricingEl)) {
      pricingEl.dataset.ssFromApplied = '1';
      return;
    }

    const fromText = 'From ' + formatMoney(product.price_min || 0);
    const currentContainer = pricingEl.querySelector('.price__current, [data-price-container]');
    const hasRangeMarkup = pricingEl.querySelector('.price__current--min, [data-price-min], [data-price-max]');

    if (currentContainer && hasRangeMarkup) {
      renderFromPrice(pricingEl, fromText);
      return;
    }

    renderFromPrice(pricingEl, fromText);
  }

  async function addWishlistButton(card, product, handle) {
    const wl = window.refactor_apps?.wishlist;
    const priceEl = card.querySelector('.productitem__price');

    if (window.Theme.customer?.is_logged_in && priceEl && wl) {
      const lists = await wl.getAllLists();
      const list = lists?.[0];

      const btn = card.querySelector('.wl-add__trigger');

      if (btn) {
        btn.parentElement.removeChild(btn);
      }

      if (list) {
        const inList = await wl.hasVariantInList(list.id, product.variant_id);

        const wlBtn = document.createElement('button');
        wlBtn.title = inList ? 'Remove from Favorites' : 'Add to Favorites';
        wlBtn.classList.add('button', 'wl-add__trigger', `wl-add__trigger--${inList ? 'active' : 'inactive'}`);

        wlBtn.innerHTML = `
  <span class="wl-icon-label" dir="ltr">
    <span class="wl-icon-label__media" aria-hidden="true">
    <svg class="icon-heart" fill="none" viewBox="0 0 24 24">
      <path stroke="currentColor" stroke-width="1.5" d="M7.87891 3.75c.67013.00152 1.33423.14051 1.9541.40918.61989.26872 1.18439.66253 1.66019 1.16016.1399.14633.3336.22972.5361.23144.2024.00163.3967-.07878.5391-.22266.9658-.9764 2.2584-1.51357 3.5957-1.50488 1.3372.00873 2.6231.5627 3.5771 1.55176.955.98998 1.5 2.33576 1.5088 3.74707.0087 1.41133-.52 2.76473-1.4629 3.76753l-6.6318 6.877c-.3018.3128-.7055.4834-1.1201.4834-.4146-.0001-.8175-.1707-1.1192-.4834l-6.63963-6.8828-.00196-.0029-.17578-.1885c-.3984-.452-.71987-.9742-.94922-1.543-.26198-.6498-.39789-1.34784-.39941-2.0537-.0015-.70608.13124-1.40537.39062-2.05664.25939-.6512.64026-1.24084 1.11817-1.73633.47787-.49543 1.04403-.88648 1.66504-1.15234.62086-.26578 1.28506-.40188 1.95508-.40039"></path>
    </svg>
    </span>
  </span>`;

        wlBtn.addEventListener('click', async () => {
          const arg = {
            id: product.id,
            variantId: product.variant_id,
            handle,
          };

          const action = inList ? wl.removeProduct : wl.addProduct;
          const result = await action(list.id, arg);

          if (result) {
            addWishlistButton(card, product, handle);
          }
        });

        priceEl.classList.add('has-wishlist');
        priceEl.appendChild(wlBtn);
      }
    }
  }

  function enhanceCard(card) {
    if (card.dataset.ssEnhanced) return;

    if (card.closest('[data-skip-ss-enhance]')) {
      card.dataset.ssEnhanced = '1';
      return;
    }

    card.dataset.ssEnhanced = '1';

    applyFromRangePriceImmediate(card);

    const handle = getProductHandle(card);
    if (!handle) return;

    fetchProduct(handle).then(function (product) {
      if (!product) return;

      applyFromRangePrice(card, product);
      debounce(() => addWishlistButton(card, product, handle))();

      const titleEl = card.querySelector('.productitem--title, .ss__custom-result__details__title');

      // --- Inject condition label ---
      if (!card.querySelector('.productitem--condition')) {
        const conditionType = getConditionType(product);
        if (conditionType) {
          const conditionEl = document.createElement('p');
          conditionEl.className = 'productitem--condition';
          const brand = product.brand ? product.brand + ' ' : '';
          if (conditionType === 'certified-pre-owned') {
            conditionEl.innerHTML = ICON_CPO + ' Certified Pre-Owned';
          } else {
            conditionEl.innerHTML = ICON_FA + ' Factory Authorized';
          }
          if (titleEl) {
            titleEl.insertAdjacentElement('afterend', conditionEl);
          }
        }
      }

      // --- Inject Yotpo bottomLine widget ---
      if (!card.querySelector('.yotpo.bottomLine')) {
        const yotpoDiv = document.createElement('div');
        yotpoDiv.className = 'yotpo bottomLine';
        yotpoDiv.style.display = 'inline-block';
        yotpoDiv.setAttribute('data-product-id', product.id);

        // Place after condition label if it exists, otherwise after title
        const conditionEl = card.querySelector('.productitem--condition');
        const insertAfter = conditionEl || titleEl;
        if (insertAfter) {
          insertAfter.insertAdjacentElement('afterend', yotpoDiv);
        } else {
          const infoEl = card.querySelector('.productitem--info, .ss__custom-result__details');
          if (infoEl) infoEl.prepend(yotpoDiv);
        }

        scheduleYotpoRefresh();
      }

      // --- Inject compare-at price if applicable ---
      if (!card.querySelector('.ss-injected-compare-price')) {
        const defaultCompareAtPrice = product.compare_at_price_max || 0;
        const isMsrpCollection = typeof product.is_msrp_collection === 'boolean'
          ? product.is_msrp_collection
          : !!product.is_specials_collection;
        const msrpComparePrice = isMsrpCollection ? parseMsrpToCents(product.msrp) : 0;
        const compareAtPrice = msrpComparePrice > 0 ? msrpComparePrice : defaultCompareAtPrice;
        const currentPrice = product.price_min;
        const maxCurrentPrice = product.price_max || currentPrice;
        const hasRealDiscount = compareAtPrice && compareAtPrice > maxCurrentPrice;

        if (hasRealDiscount) {
          const compareEl = document.createElement('s');
          compareEl.className = 'ss-injected-compare-price price__compare-at visible';
          compareEl.textContent = formatMoney(compareAtPrice);

          const pricingEl = card.querySelector(PRICING_SELECTOR);
          if (pricingEl) {
            pricingEl.appendChild(compareEl);
          }
        }
      }

      // --- Hide price for Used out-of-stock products ---
      const productTags = (product.tags || '').toLowerCase();
      if (productTags.split(',').some(function (t) { return t.trim() === 'used'; }) && product.available === false) {
        const pricingEl = card.querySelector(PRICING_SELECTOR);
        if (pricingEl) {
          pricingEl.style.display = 'none';
        }
      }
    });
  }

  const CARD_SELECTOR_UNPROCESSED = [
    '.ss__custom-result:not([data-ss-enhanced])',
    '.ss__result:not([data-ss-enhanced])',
    '.productitem:not([data-ss-enhanced])',
  ].join(', ');

  const CARD_SELECTOR_ALL = [
    '.ss__custom-result',
    '.ss__result',
    '.productitem',
  ].join(', ');

  /**
   * Find all unprocessed result cards inside the given container and enhance them.
   */
  function enhanceAllCards(container) {
    if (!container) return;

    container.querySelectorAll(CARD_SELECTOR_UNPROCESSED).forEach(enhanceCard);

    if (isFromRangeEnabled()) {
      container.querySelectorAll(CARD_SELECTOR_ALL).forEach(function (card) {
        applyFromRangePriceImmediate(card);
      });
    }
  }

  /**
   * Remove the skeleton overlay once Snap's product cards are in the DOM.
   * No transition — products are already rendered underneath.
   */
  function removeSkeleton(container) {
    // Skeleton is a sibling of container inside .ss-skeleton-wrapper
    const wrapper = container.parentElement;
    const skeleton = wrapper ? wrapper.querySelector('.ss-skeleton') : null;
    if (!skeleton) return;
    skeleton.remove();
    container.style.minHeight = '';
  }

  /**
   * Start observing #searchspring-templates for Snap re-renders.
   */
  function init() {
    const container = document.getElementById('searchspring-templates');
    if (container) {
      // Process any cards already in the DOM on init
      enhanceAllCards(container);

      // If cards already exist at init time, remove the skeleton immediately
      const hasCardsAtInit = container.querySelector(
        '.ss__result, .ss__custom-result, .ss__results .productitem'
      );
      if (hasCardsAtInit) {
        removeSkeleton(container);
      }

      const observer = new MutationObserver(function () {
        // Remove skeleton only once actual result cards exist in the DOM
        // (Snap renders them underneath the absolute-positioned skeleton)
        const hasCards = container.querySelector(
          '.ss__result, .ss__custom-result, .ss__results .productitem'
        );
        if (hasCards) {
          removeSkeleton(container);
        }
        enhanceAllCards(container);
      });

      observer.observe(container, { childList: true, subtree: true });
    }

    initAutocomplete();
  }

  /**
   * Watch for the SS autocomplete dropdown and apply From-price formatting to its results.
   * The autocomplete panel is created dynamically by Snap outside #searchspring-templates.
   */
  function initAutocomplete() {
    if (!isFromRangeEnabled()) return;

    const AC_CONTAINER_SELECTOR = '.ss__autocomplete, .ss__autocomplete__results, [class*="ss__autocomplete"]';

    function applyFromRangeInContainer(container) {
      if (!container) return;
      container.querySelectorAll(CARD_SELECTOR_ALL).forEach(function (card) {
        applyFromRangePriceImmediate(card);
      });
    }

    function processAcContainer(acEl) {
      if (!acEl || acEl.dataset.ssAutocompleteObserved === '1') return;

      applyFromRangeInContainer(acEl);
      acEl.dataset.ssAutocompleteObserved = '1';

      const acObserver = new MutationObserver(function () {
        applyFromRangeInContainer(acEl);
      });
      acObserver.observe(acEl, { childList: true, subtree: true });
    }

    // Handle autocomplete containers already in the DOM
    document.querySelectorAll(AC_CONTAINER_SELECTOR).forEach(processAcContainer);

    // Watch for Snap mounting the autocomplete panel
    const bodyObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches(AC_CONTAINER_SELECTOR)) {
            processAcContainer(node);
          } else if (node.querySelector) {
            node.querySelectorAll(AC_CONTAINER_SELECTOR).forEach(processAcContainer);
          }
        });
      });
    });

    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // --- Sticky top adjustment for collection page ---
  let ticking = false;

  function getHeaderStickyOffset() {
    const selectors = ['.site-navigation-wrapper', 'header.site-header'];
    let maxBottom = 0;

    selectors.forEach(function (selector) {
      const el = document.querySelector(selector);
      if (!el) return;

      const rect = el.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top >= window.innerHeight) return;

      const visibleBottom = Math.max(0, Math.min(rect.bottom, window.innerHeight));
      maxBottom = Math.max(maxBottom, visibleBottom);
    });

    return Math.round(maxBottom);
  }

  function getViewportHeight() {
    if (window.visualViewport && window.visualViewport.height) {
      return Math.round(window.visualViewport.height);
    }
    return window.innerHeight;
  }

  function adjustStickyTop() {
    const searchSidebar = document.querySelector('.ss__search__sidebar');
    const searchSort = document.querySelector('.ss__theme__global .ss__search__content__toolbar--middle-toolbar');

    if (!searchSidebar) return;

    if (window.innerWidth >= 861) {
      const stickyOffset = getHeaderStickyOffset();
      const viewportHeight = getViewportHeight();
      const maxHeight = Math.max(0, viewportHeight - stickyOffset);

      searchSidebar.style.top = stickyOffset + 'px';
      searchSidebar.style.maxHeight = maxHeight + 'px';
      searchSidebar.style.overflowY = 'auto';

      if (searchSort) {
        searchSort.style.top = stickyOffset + 'px';
      }
    } else {
      searchSidebar.style.top = '';
      searchSidebar.style.maxHeight = '';
      searchSidebar.style.overflowY = '';

      if (searchSort) {
        searchSort.style.top = '';
      }
    }
  }

  function requestTick() {
    if (!ticking) {
      requestAnimationFrame(function() {
        adjustStickyTop();
        ticking = false;
      });
      ticking = true;
    }
  }

  function runStickySyncBurst(durationMs) {
    const start = Date.now();

    function loop() {
      requestTick();
      if (Date.now() - start < durationMs) {
        requestAnimationFrame(loop);
      }
    }

    loop();
  }

  function bindHeaderStickyWatchers() {
    const watchSelectors = ['header.site-header', '.site-navigation-wrapper'];
    const watched = [];

    watchSelectors.forEach(function (selector) {
      const el = document.querySelector(selector);
      if (!el) return;
      watched.push(el);
    });

    if (!watched.length) return;

    watched.forEach(function (el) {
      ['transitionrun', 'transitionstart', 'transitionend', 'animationstart', 'animationend'].forEach(function (evt) {
        el.addEventListener(evt, function () {
          runStickySyncBurst(450);
        });
      });
    });

    const headerObserver = new MutationObserver(function () {
      runStickySyncBurst(250);
    });

    watched.forEach(function (el) {
      headerObserver.observe(el, {
        attributes: true,
        attributeFilter: ['class', 'style']
      });
    });

    if (typeof ResizeObserver === 'function') {
      const resizeObserver = new ResizeObserver(function () {
        requestTick();
      });

      watched.forEach(function (el) {
        resizeObserver.observe(el);
      });
    }
  }

  // Initial adjustment
  adjustStickyTop();
  bindHeaderStickyWatchers();

  // Adjust on resize
  window.addEventListener('resize', adjustStickyTop);

  // Adjust on scroll with throttling
  window.addEventListener('scroll', requestTick);
})();
